// =====================================================================
// FiscalEmitentesDialog (NF-e Focus / S97) — onboarding self-service do emitente.
// Lista as clínicas já cadastradas + formulário "Cadastrar clínica" que sobe o
// certificado A1 (.pfx) + dados fiscais → backend chama a Focus (POST /v2/empresas)
// e guarda o token cifrado. Escala p/ N clínicas sem mexer em env. Padrão MUI v4.
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Typography, Box, CircularProgress, Divider, IconButton, Tooltip, Chip
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import { toast } from "react-toastify";
import {
  listFiscalEmitentes, createFiscalEmitente, deleteFiscalEmitente,
  updateFiscalEmitenteConfig, financeErrorMessage
} from "../../services/financeService";

const REGIMES = [
  { value: "3", label: "Normal (não Simples)" },
  { value: "1", label: "Simples Nacional" },
  { value: "4", label: "MEI" }
];
const EMPTY = {
  unitId: "", razaoSocial: "", nomeFantasia: "", cnpj: "", inscricaoMunicipal: "",
  codigoMunicipio: "", municipioNome: "", uf: "", regimeTributario: "3",
  logradouro: "", numero: "", bairro: "", cep: "",
  modelo: "nfse",
  itemListaServico: "", codigoTributarioMunicipio: "", codigoTributacaoNacional: "", codigoCnae: "",
  aliquota: "", discriminacaoPadrao: "",
  loginResponsavel: "", senhaResponsavel: "",
  ambiente: "homologacao"
};
// Exemplo pronto (dados reais da NFS-e do Pablo em Itajaí) — poupa digitação no piloto.
// Itajaí migrou pro padrão NACIONAL (nfsen); cód. tributação nacional do ISSQN = 040101 (Medicina).
const ITAJAI = {
  ...EMPTY, unitId: "2", razaoSocial: "INSTITUTO MELO LTDA", nomeFantasia: "INSTITUTO MELO",
  cnpj: "38946855000182", inscricaoMunicipal: "333785", codigoMunicipio: "4208203",
  municipioNome: "ITAJAI", uf: "SC", regimeTributario: "3",
  logradouro: "OSVALDO REIS", numero: "3281", bairro: "PRAIA BRAVA", cep: "88306773",
  modelo: "nfsen",
  itemListaServico: "0401", codigoTributarioMunicipio: "040101", codigoTributacaoNacional: "040101",
  aliquota: "2", discriminacaoPadrao: "Referente a consulta médica", ambiente: "homologacao"
};

const FiscalEmitentesDialog = ({ open, onClose, onSaved, units = [], hasUnits = false }) => {
  const [emitentes, setEmitentes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [certFile, setCertFile] = useState(null);
  const [senha, setSenha] = useState("");
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);        // [S100] emitente em edição de config fiscal (IBPT)
  const [cfgSaving, setCfgSaving] = useState(false);

  // Captura o valor SÍNCRONO (fora do updater) + guarda contra e.target null —
  // o password-manager pode disparar eventos sem target, o que gerava "reading 'value' of null".
  const set = (k) => (e) => {
    const v = e && e.target ? e.target.value : "";
    setF((s) => ({ ...s, [k]: v }));
  };

  const load = async () => {
    setLoading(true);
    try {
      const d = await listFiscalEmitentes();
      setEmitentes(Array.isArray(d?.emitentes) ? d.emitentes : []);
    } catch (e) { setEmitentes([]); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (open) { load(); setShowForm(false); setF(EMPTY); setCertFile(null); setSenha(""); }
  }, [open]);

  const handleSave = async () => {
    if (!f.razaoSocial || !f.cnpj) { toast.error("Informe razão social e CNPJ."); return; }
    const temCert = !!certFile;
    const temLogin = !!(f.loginResponsavel && f.senhaResponsavel);
    if (!temCert && !temLogin) { toast.error("Anexe o certificado A1 OU informe login e senha do portal."); return; }
    if (temCert && !senha) { toast.error("Informe a senha do certificado."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.keys(f).forEach((k) => { if (f[k] !== "" && f[k] != null) fd.append(k, f[k]); });
      fd.append("senha", senha);
      fd.append("cert", certFile);
      await createFiscalEmitente(fd);
      toast.success("Clínica cadastrada na Focus. ✅");
      setShowForm(false); setF(EMPTY); setCertFile(null); setSenha("");
      await load();
      onSaved && onSaved();
    } catch (e) {
      toast.error(financeErrorMessage(e));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover este emitente? (não cancela notas já emitidas)")) return;
    try { await deleteFiscalEmitente(id); await load(); onSaved && onSaved(); }
    catch (e) { toast.error(financeErrorMessage(e)); }
  };

  // [S100] Config fiscal (percentuais IBPT / alíquota / discriminação) — update LOCAL, sem Focus/cert.
  const num2str = (v) => (v == null || v === "" ? "" : String(Number(v)));
  const openCfg = (e) => setCfg({
    id: e.id, _razao: e.razaoSocial, _iss: num2str(e.aliquota),
    aliquota: num2str(e.aliquota), discriminacaoPadrao: e.discriminacaoPadrao || "",
    pctTribFederais: num2str(e.pctTribFederais),
    pctTribEstaduais: num2str(e.pctTribEstaduais),
    pctTribMunicipais: num2str(e.pctTribMunicipais)
  });
  const setCfgField = (k) => (e) => {
    const v = e && e.target ? e.target.value : "";
    setCfg((s) => ({ ...s, [k]: v }));
  };
  const handleSaveCfg = async () => {
    if (!cfg) return;
    setCfgSaving(true);
    try {
      await updateFiscalEmitenteConfig(cfg.id, {
        aliquota: cfg.aliquota,
        discriminacaoPadrao: cfg.discriminacaoPadrao,
        pctTribFederais: cfg.pctTribFederais,
        pctTribEstaduais: cfg.pctTribEstaduais,
        pctTribMunicipais: cfg.pctTribMunicipais
      });
      toast.success("Config fiscal atualizada. ✅");
      setCfg(null);
      await load();
      onSaved && onSaved();
    } catch (e) {
      toast.error(financeErrorMessage(e));
    } finally { setCfgSaving(false); }
  };

  return (
    <>
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Emitentes de nota fiscal (NF-e)</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Cadastre cada clínica (CNPJ) uma vez — o certificado vai direto pra Focus, com segurança.
        </Typography>

        {loading ? (
          <Box textAlign="center" py={2}><CircularProgress size={22} /></Box>
        ) : emitentes.length === 0 ? (
          <Typography variant="body2" style={{ margin: "8px 0" }}>Nenhuma clínica cadastrada ainda.</Typography>
        ) : (
          <Box my={1}>
            {emitentes.map((e) => (
              <Box key={e.id} display="flex" alignItems="center" style={{ gap: 8, padding: "4px 0" }}>
                <Box flexGrow={1}>
                  <Typography variant="body2"><strong>{e.razaoSocial}</strong> — {e.cnpj}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {e.municipioNome}/{e.uf} · {e.modelo === "nfsen" ? "Nacional" : "Municipal"} · ISS {e.aliquota || 0}%
                  </Typography>
                </Box>
                <Chip size="small" color={e.ambiente === "producao" ? "primary" : "default"}
                  label={e.ambiente === "producao" ? "Produção" : "Homologação"} />
                <Tooltip title="Config fiscal (IBPT / alíquota / discriminação)"><IconButton size="small" onClick={() => openCfg(e)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Remover"><IconButton size="small" onClick={() => handleDelete(e.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              </Box>
            ))}
          </Box>
        )}

        <Divider style={{ margin: "12px 0" }} />

        {!showForm ? (
          <Box display="flex" style={{ gap: 8 }}>
            <Button variant="contained" color="primary" onClick={() => setShowForm(true)}
              style={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}>+ Cadastrar clínica</Button>
            <Button onClick={() => { setF(ITAJAI); setShowForm(true); }} color="primary" style={{ textTransform: "none" }}>Preencher com Itajaí (exemplo)</Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Nova clínica (emitente)</Typography>
            <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
              {hasUnits && (
                <TextField select label="Unidade" value={f.unitId} onChange={set("unitId")} margin="dense" style={{ minWidth: 150 }}>
                  <MenuItem value=""><em>—</em></MenuItem>
                  {units.map((u) => <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>)}
                </TextField>
              )}
              <TextField label="Razão social" value={f.razaoSocial} onChange={set("razaoSocial")} margin="dense" style={{ flexGrow: 1, minWidth: 220 }} />
              <TextField label="Nome fantasia" value={f.nomeFantasia} onChange={set("nomeFantasia")} margin="dense" style={{ minWidth: 160 }} />
            </Box>
            <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
              <TextField label="CNPJ" value={f.cnpj} onChange={set("cnpj")} margin="dense" style={{ minWidth: 160 }} />
              <TextField label="Inscrição municipal" value={f.inscricaoMunicipal} onChange={set("inscricaoMunicipal")} margin="dense" style={{ minWidth: 150 }} />
              <TextField select label="Regime" value={f.regimeTributario} onChange={set("regimeTributario")} margin="dense" style={{ minWidth: 170 }}>
                {REGIMES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </TextField>
            </Box>
            <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
              <TextField label="Município" value={f.municipioNome} onChange={set("municipioNome")} margin="dense" style={{ minWidth: 150 }} />
              <TextField label="UF" value={f.uf} onChange={set("uf")} margin="dense" style={{ width: 70 }} inputProps={{ maxLength: 2 }} />
              <TextField label="Cód. município (IBGE)" value={f.codigoMunicipio} onChange={set("codigoMunicipio")} margin="dense" style={{ minWidth: 150 }} />
            </Box>
            <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
              <TextField label="Logradouro" value={f.logradouro} onChange={set("logradouro")} margin="dense" style={{ flexGrow: 1, minWidth: 180 }} />
              <TextField label="Nº" value={f.numero} onChange={set("numero")} margin="dense" style={{ width: 90 }} />
              <TextField label="Bairro" value={f.bairro} onChange={set("bairro")} margin="dense" style={{ minWidth: 140 }} />
              <TextField label="CEP" value={f.cep} onChange={set("cep")} margin="dense" style={{ width: 120 }} />
            </Box>
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
              Dados fiscais do serviço (do seu contador — cada prefeitura tem os seus):
            </Typography>
            <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
              <TextField select label="Modelo da nota" value={f.modelo} onChange={set("modelo")} margin="dense" style={{ minWidth: 200 }}
                helperText={f.modelo === "nfsen" ? "Padrão nacional (ADN)" : "Padrão da prefeitura"}>
                <MenuItem value="nfse">NFS-e Municipal</MenuItem>
                <MenuItem value="nfsen">NFS-e Nacional</MenuItem>
              </TextField>
              {f.modelo === "nfsen" ? (
                <TextField label="Cód. tributação nacional" value={f.codigoTributacaoNacional} onChange={set("codigoTributacaoNacional")} margin="dense" style={{ minWidth: 190 }} helperText="ISSQN nacional, ex.: 040101" />
              ) : (
                <>
                  <TextField label="Item LC 116" value={f.itemListaServico} onChange={set("itemListaServico")} margin="dense" style={{ width: 120 }} helperText="ex.: 0401 / 04.03" />
                  <TextField label="Cód. serviço município" value={f.codigoTributarioMunicipio} onChange={set("codigoTributarioMunicipio")} margin="dense" style={{ minWidth: 150 }} helperText="se a prefeitura usar" />
                  <TextField label="CNAE" value={f.codigoCnae} onChange={set("codigoCnae")} margin="dense" style={{ width: 140 }} helperText="ex.: 863050200 (JP)" />
                </>
              )}
              <TextField label="ISS %" value={f.aliquota} onChange={set("aliquota")} margin="dense" type="number" style={{ width: 90 }} />
              <TextField select label="Ambiente" value={f.ambiente} onChange={set("ambiente")} margin="dense" style={{ minWidth: 150 }}>
                <MenuItem value="homologacao">Homologação (teste)</MenuItem>
                <MenuItem value="producao">Produção (valor fiscal)</MenuItem>
              </TextField>
            </Box>
            <TextField label="Discriminação padrão" value={f.discriminacaoPadrao} onChange={set("discriminacaoPadrao")} fullWidth margin="dense" />

            <Box mt={1} display="flex" alignItems="center" style={{ gap: 12, flexWrap: "wrap" }}>
              <Button component="label" variant="outlined" size="small">
                {certFile ? "Trocar certificado" : "📎 Certificado A1 (.pfx)"}
                <input type="file" accept=".pfx,.p12" hidden
                  onChange={(e) => { const x = e.target.files && e.target.files[0]; e.target.value = ""; setCertFile(x || null); }} />
              </Button>
              {certFile && <Typography variant="caption">{certFile.name}</Typography>}
              <TextField label="Senha do certificado" type="password" value={senha} onChange={(e) => setSenha(e.target.value)} margin="dense" style={{ minWidth: 180 }} />
            </Box>
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 8 }}>
              — OU — login e senha do portal da prefeitura (municípios sem certificado, ex.: João Pessoa/DSF):
            </Typography>
            <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
              <TextField label="Login do portal" value={f.loginResponsavel} onChange={set("loginResponsavel")} margin="dense" style={{ minWidth: 200 }} autoComplete="off" />
              <TextField label="Senha do portal" type="password" value={f.senhaResponsavel} onChange={set("senhaResponsavel")} margin="dense" style={{ minWidth: 200 }} autoComplete="new-password" />
            </Box>
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
              🔒 Certificado ou credenciais vão direto pra Focus (não ficam no nosso servidor). Comece em Homologação.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Fechar</Button>
        {showForm && (
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
            style={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}>
            Cadastrar na Focus
          </Button>
        )}
      </DialogActions>
    </Dialog>

    <Dialog open={!!cfg} onClose={cfgSaving ? undefined : () => setCfg(null)} maxWidth="xs" fullWidth>
      <DialogTitle>Config fiscal{cfg?._razao ? ` — ${cfg._razao}` : ""}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Percentuais IBPT (Lei da Transparência) que saem na nota. Deixe vazio pra usar a
          aproximação padrão — preencha com os valores oficiais do seu contador.
        </Typography>
        <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
          <TextField label="Trib. federais %" value={cfg?.pctTribFederais || ""} onChange={setCfgField("pctTribFederais")}
            margin="dense" type="number" style={{ width: 140 }} placeholder="aprox. 13,45" InputLabelProps={{ shrink: true }} />
          <TextField label="Trib. estaduais %" value={cfg?.pctTribEstaduais || ""} onChange={setCfgField("pctTribEstaduais")}
            margin="dense" type="number" style={{ width: 140 }} placeholder="aprox. 0,00" InputLabelProps={{ shrink: true }} />
          <TextField label="Trib. municipais %" value={cfg?.pctTribMunicipais || ""} onChange={setCfgField("pctTribMunicipais")}
            margin="dense" type="number" style={{ width: 150 }} placeholder={`aprox. ${cfg?._iss || 0}`} InputLabelProps={{ shrink: true }} />
        </Box>
        <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
          <TextField label="ISS %" value={cfg?.aliquota || ""} onChange={setCfgField("aliquota")}
            margin="dense" type="number" style={{ width: 100 }} />
        </Box>
        <TextField label="Discriminação padrão" value={cfg?.discriminacaoPadrao || ""} onChange={setCfgField("discriminacaoPadrao")}
          fullWidth margin="dense" />
        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 6 }}>
          Vale só pra NOTAS NOVAS. Não mexe na Focus nem no certificado.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCfg(null)} disabled={cfgSaving}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleSaveCfg} disabled={cfgSaving}
          startIcon={cfgSaving ? <CircularProgress size={16} /> : null}
          style={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}>Salvar</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default FiscalEmitentesDialog;
